import { getVastuInfo, getZoneColor } from "./vastu";
import { evaluateMarker, STATUS_COLORS } from "./rooms";
import { sectorLabelAnchor, formatDeg } from "./sectors";

// Render plan + overlay to an offscreen canvas with a white, print-friendly background
export function renderSnapshot({ image, imageDims, rotation, polygon, sectors, center, sectorCount, showOverlay = true, showDegrees = true, markers = [], maxSize = 2400, purushaImg = null }) {
  const w = imageDims?.width || 800;
  const h = imageDims?.height || 600;
  const u = Math.max(w, h) / 900;
  // Room for the divisions circle + periphery labels sitting outside the outline
  const R = (center && sectors[0]?.radius) || 0;
  const labelMargin = 90 * u;
  const pad = Math.max(
    110 * u,
    R + labelMargin - (center?.x ?? 0),
    center ? center.x + R + labelMargin - w : 0,
    R + labelMargin - (center?.y ?? 0),
    center ? center.y + R + labelMargin - h : 0,
  );
  const scale = Math.min(1, maxSize / Math.max(w + pad * 2, h + pad * 2));

  const canvas = document.createElement("canvas");
  canvas.width = Math.round((w + pad * 2) * scale);
  canvas.height = Math.round((h + pad * 2) * scale);
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(pad, pad);

  if (image) {
    ctx.drawImage(image, 0, 0, w, h);
  }

  if (polygon.length >= 3) {
    ctx.beginPath();
    polygon.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = "rgba(217, 119, 6, 0.08)";
    ctx.fill();
    ctx.setLineDash([8 * u, 5 * u]);
    ctx.strokeStyle = "#B45309";
    ctx.lineWidth = 2.5 * u;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (showOverlay) {
    for (const s of sectors) {
      const zc = getZoneColor(s.code);
      ctx.beginPath();
      s.vertices.forEach((v, i) => (i === 0 ? ctx.moveTo(v.x, v.y) : ctx.lineTo(v.x, v.y)));
      ctx.closePath();
      ctx.fillStyle = hexToRgba(zc, 0.28);
      ctx.fill();
      ctx.strokeStyle = hexToRgba(zc, 0.9);
      ctx.lineWidth = 1.2 * u;
      ctx.stroke();
    }

    // Divisions circle outline + faint concentric chakra rings (centred on Brahmasthan)
    if (sectors[0]?.radius) {
      const R = sectors[0].radius;
      ctx.beginPath();
      ctx.arc(center.x, center.y, R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(245, 158, 11, 0.6)";
      ctx.lineWidth = 1.6 * u;
      ctx.stroke();
      for (const f of [0.4, 0.68]) {
        ctx.beginPath();
        ctx.arc(center.x, center.y, R * f, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(245, 158, 11, 0.28)";
        ctx.lineWidth = 1 * u;
        ctx.stroke();
      }
      // Degree ticks around the outer ring for a dial-like chakra
      const drawTick = (pt, len, op) => {
        const d = Math.hypot(pt.x - center.x, pt.y - center.y) || 1;
        const ux = (pt.x - center.x) / d, uy = (pt.y - center.y) / d;
        ctx.beginPath();
        ctx.moveTo(center.x + ux * R, center.y + uy * R);
        ctx.lineTo(center.x + ux * (R + len), center.y + uy * (R + len));
        ctx.strokeStyle = `rgba(245, 158, 11, ${op})`;
        ctx.lineWidth = 1.2 * u;
        ctx.stroke();
      };
      sectors.forEach((s) => drawTick(s.vertices[1], R * 0.045, 0.7));
      if (sectors.length <= 16) {
        sectors.forEach((s) => s.vertices.slice(2, 9).forEach((v) => drawTick(v, R * 0.022, 0.4)));
      }
      // Emphasised cardinal ticks (N / E / S / W) — longer & bolder for quick orientation
      ctx.lineCap = "round";
      [0, 90, 180, 270].forEach((ca) => {
        const a = ((ca + rotation) * Math.PI) / 180;
        const ux = Math.sin(a), uy = -Math.cos(a);
        ctx.beginPath();
        ctx.moveTo(center.x + ux * R, center.y + uy * R);
        ctx.lineTo(center.x + ux * (R + R * 0.095), center.y + uy * (R + R * 0.095));
        ctx.strokeStyle = "rgba(245, 158, 11, 1)";
        ctx.lineWidth = 3 * u;
        ctx.stroke();
      });
      ctx.lineCap = "butt";
    }

    const fontSize = (sectorCount === 32 ? 10 : sectorCount === 16 ? 13 : 16) * u;
    const tick = 8 * u;
    const gap = (sectorCount === 32 ? 14 : sectorCount === 16 ? 18 : 26) * u;
    const stack = sectorCount === 8;
    for (const s of sectors) {
      const info = getVastuInfo(s.code);
      const zc = getZoneColor(s.code);
      const { boundary, anchor, ux, uy } = sectorLabelAnchor(s, center, tick + gap);
      ctx.beginPath();
      ctx.moveTo(boundary.x, boundary.y);
      ctx.lineTo(boundary.x + ux * tick, boundary.y + uy * tick);
      ctx.strokeStyle = hexToRgba(zc, 0.95);
      ctx.lineWidth = 1.4 * u;
      ctx.stroke();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineJoin = "round";
      ctx.font = `700 ${fontSize}px "Fira Code", monospace`;
      ctx.lineWidth = 4 * u;
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      const cy = anchor.y - (stack ? 6 * u : 0);
      ctx.strokeText(s.code, anchor.x, cy);
      ctx.fillStyle = "#0F172A";
      ctx.fillText(s.code, anchor.x, cy);
      if (stack) {
        ctx.font = `600 ${11 * u}px "Outfit", sans-serif`;
        ctx.lineWidth = 4 * u;
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.strokeText(info.sanskrit, anchor.x, anchor.y + 8 * u);
        ctx.fillStyle = darken(zc);
        ctx.fillText(info.sanskrit, anchor.x, anchor.y + 8 * u);
      }
      // Degree of the division centre — helps identify orientation at a glance
      if (showDegrees) {
        const degText = formatDeg(s.centerDeg);
        const degY = stack ? anchor.y + 20 * u : cy + fontSize * 0.92;
        ctx.font = `600 ${Math.max(8 * u, fontSize * 0.72)}px "Fira Code", monospace`;
        ctx.lineWidth = 3.5 * u;
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.strokeText(degText, anchor.x, degY);
        ctx.fillStyle = "#B45309";
        ctx.fillText(degText, anchor.x, degY);
      }
    }
  }

  // Vastu Purusha outline overlay (head → NE, feet → SW), scaled to the polygon and aligned to true north
  if (purushaImg && center && polygon.length >= 3) {
    const xs = polygon.map((p) => p.x), ys = polygon.map((p) => p.y);
    const side = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) * 1.05;
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.globalAlpha = 0.9;
    ctx.drawImage(purushaImg, -side / 2, -side / 2, side, side);
    ctx.restore();
  }

  if (center && polygon.length >= 3) {
    ctx.beginPath();
    ctx.arc(center.x, center.y, 18 * u, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(236, 72, 153, 0.2)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(center.x, center.y, 7 * u, 0, Math.PI * 2);
    ctx.fillStyle = "#DB2777";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(center.x, center.y, 2.5 * u, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
  }

  polygon.forEach((p, i) => {
    ctx.beginPath();
    ctx.arc(p.x, p.y, 9 * u, 0, Math.PI * 2);
    ctx.fillStyle = "#D97706";
    ctx.fill();
    ctx.lineWidth = 2 * u;
    ctx.strokeStyle = "#FFFFFF";
    ctx.stroke();
    ctx.font = `700 ${9 * u}px "Fira Code", monospace`;
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), p.x, p.y);
  });

  for (const m of markers) {
    const ev = evaluateMarker(m, polygon.length >= 3 ? center : null, rotation);
    const color = STATUS_COLORS[ev.status];
    const r = 11 * u;
    ctx.beginPath();
    ctx.arc(m.x, m.y, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 2 * u;
    ctx.strokeStyle = "#FFFFFF";
    ctx.stroke();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (ev.status === "conflict") {
      ctx.font = `800 ${13 * u}px "Fira Code", monospace`;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText("!", m.x, m.y + 0.5 * u);
    }
    const label = `${ev.room.label}${ev.direction ? ` · ${ev.direction}` : ""}`;
    ctx.font = `600 ${11 * u}px "Outfit", sans-serif`;
    ctx.textBaseline = "alphabetic";
    ctx.lineWidth = 4 * u;
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.strokeText(label, m.x, m.y - r - 6 * u);
    ctx.fillStyle = ev.status === "conflict" ? "#B91C1C" : "#0F172A";
    ctx.fillText(label, m.x, m.y - r - 6 * u);
  }

  ctx.restore();
  const arrowScale = canvas.width / 1100;
  drawNorthArrow(ctx, canvas.width - 60 * arrowScale, 60 * arrowScale, arrowScale, rotation);
  return canvas;
}

function drawNorthArrow(ctx, cx, cy, scale, rotation) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.beginPath();
  ctx.arc(0, 0, 34, 0, Math.PI * 2);
  ctx.fillStyle = "#F8FAFC";
  ctx.fill();
  ctx.strokeStyle = "#CBD5E1";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.save();
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(0, -24); ctx.lineTo(8, 12); ctx.lineTo(0, 6); ctx.lineTo(-8, 12); ctx.closePath();
  ctx.fillStyle = "#B45309";
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(0, -24); ctx.lineTo(8, 12); ctx.lineTo(0, 6); ctx.closePath();
  ctx.fillStyle = "#F59E0B";
  ctx.fill();
  ctx.font = `700 11px "Fira Code", monospace`;
  ctx.fillStyle = "#B45309";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("N", 0, -30);
  ctx.restore();
  ctx.restore();
}

function hexToRgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

function darken(hex) {
  const n = parseInt(hex.slice(1), 16);
  const f = 0.65;
  return `rgb(${Math.round(((n >> 16) & 255) * f)}, ${Math.round(((n >> 8) & 255) * f)}, ${Math.round((n & 255) * f)})`;
}

export function downloadCanvasPNG(canvas, filename) {
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
